import { useState } from "react";

import { KvaPeriod, RateComponent } from "../../../types/proposalDocuments.types";
import {
  AVAILABLE_RATES,
  AVAILABLE_UNITS,
  KVA_PERIOD_MULTIPLIERS,
  KVA_PERIOD_OPTIONS,
  RateUnit,
  computeRateCost,
  getUnitByLabel,
} from "../proposalDocuments.constants";

interface RateRowProps {
  rate: RateComponent;
  onChange: (updated: RateComponent) => void;
  onRemove: () => void;
  errorMessage?: string;
}

const RateRow = ({ rate, onChange, onRemove, errorMessage }: RateRowProps) => {
  const hasError = !!errorMessage;

  // Local display state for the two raw entry fields, kept in sync with
  // `rate` during render below rather than bound directly to it - this keeps
  // the inputs controlled (so external updates like loading an existing
  // quote, or a sibling row being removed, are reflected) without fighting
  // the user mid-keystroke, which is what a raw `value={rate.value}` binding
  // did before it was stripped out. Every keystroke round-trips through
  // `onChange` -> parent state -> this component's props, so the resync
  // only actually replaces the typed string when its numeric value differs
  // from the incoming prop - otherwise typing "24." would get snapped back
  // to "24" the moment the round-tripped prop update lands.
  const [valueInput, setValueInput] = useState(String(rate.value));
  const [consumptionInput, setConsumptionInput] = useState(String(rate.consumption));

  const [prevRateValue, setPrevRateValue] = useState(rate.value);
  const [prevRateConsumption, setPrevRateConsumption] = useState(rate.consumption);

  if (rate.value !== prevRateValue) {
    setPrevRateValue(rate.value);
    if (Number(valueInput) !== rate.value) setValueInput(String(rate.value));
  }

  if (rate.consumption !== prevRateConsumption) {
    setPrevRateConsumption(rate.consumption);
    if (Number(consumptionInput) !== rate.consumption)
      setConsumptionInput(String(rate.consumption));
  }

  const getUnitById = (id: string): RateUnit | undefined =>
    AVAILABLE_UNITS.find((u) => u.id === id);

  // Only meaningful for KVA (the one unit with `hasPeriodSelector`), but
  // harmless to pass through for every other unit's transform, which
  // ignores it.
  const periodMultiplier = KVA_PERIOD_MULTIPLIERS[rate.kvaPeriod];

  const handleLabelChange = (label: string) => {
    const selectedRate = AVAILABLE_RATES.find((r) => r.label === label);
    if (!selectedRate) return;

    const defaultUnit = getUnitById(selectedRate.defaultRate);
    const newUnit = defaultUnit ? defaultUnit.label : rate.unit;

    onChange({
      ...rate,
      label: selectedRate.label,
      unit: newUnit,
      hasConsumption: defaultUnit ? defaultUnit.hasConsumption : rate.hasConsumption,
      nonCommodity: selectedRate.nonCommodity,
      estimatedAnnualCost: computeRateCost(rate.consumption, rate.value, newUnit, periodMultiplier),
    });
  };

  const handleValueChange = (value: string) => {
    setValueInput(value);
    const newValue = value === "" ? 0 : Number(value);
    onChange({
      ...rate,
      value: newValue,
      estimatedAnnualCost: computeRateCost(rate.consumption, newValue, rate.unit, periodMultiplier),
    });
  };

  const handleConsumptionChange = (consumption: string) => {
    setConsumptionInput(consumption);
    const newConsumption = consumption === "" ? 0 : Number(consumption);
    onChange({
      ...rate,
      consumption: newConsumption,
      estimatedAnnualCost: computeRateCost(newConsumption, rate.value, rate.unit, periodMultiplier),
    });
  };

  const handleUnitChange = (unit: string) => {
    const newUnit = getUnitByLabel(unit);
    onChange({
      ...rate,
      unit,
      hasConsumption: newUnit ? newUnit.hasConsumption : rate.hasConsumption,
      estimatedAnnualCost: computeRateCost(rate.consumption, rate.value, unit, periodMultiplier),
    });
  };

  const handleKvaPeriodChange = (kvaPeriod: KvaPeriod) => {
    onChange({
      ...rate,
      kvaPeriod,
      estimatedAnnualCost: computeRateCost(
        rate.consumption,
        rate.value,
        rate.unit,
        KVA_PERIOD_MULTIPLIERS[kvaPeriod],
      ),
    });
  };

  const handleHiddenChange = (hidden: boolean) => {
    onChange({ ...rate, hidden });
  };

  return (
    <div
      className={`rate-row d-flex align-items-center gap-2 ${rate.hidden ? "rate-row--hidden" : ""}`}
    >
      <div className="rate-row__field-group">
        <label htmlFor={`rate-label-${rate.label}`} className="rate-row__label">
          Rate Type
        </label>
        <select
          id={`rate-label-${rate.label}`}
          className={`rate-row__rate-select ${hasError ? "is-invalid" : ""}`}
          value={rate.label}
          onChange={(e) => {
            handleLabelChange(e.target.value);
          }}
          aria-label="Rate type"
        >
          {AVAILABLE_RATES.map((r) => (
            <option key={r.label} value={r.label}>
              {r.label}
            </option>
          ))}
        </select>
      </div>

      <div className="rate-row__field-group">
        <label className="rate-row__label">
          Rate Value ({getUnitByLabel(rate.unit)?.currency ?? "?"}/unit)
        </label>
        <input
          type="number"
          className={`rate-row__rate-value ${hasError ? "is-invalid" : ""}`}
          placeholder="e.g. 24.63"
          value={valueInput}
          onChange={(e) => {
            handleValueChange(e.target.value);
          }}
          aria-label="Rate value"
        />
      </div>

      {rate.hasConsumption && (
        <div className="rate-row__field-group">
          <label className="rate-row__label" htmlFor={`rate-consumption-${rate.label}`}>
            Consumption
          </label>
          {rate.nonCommodity ? (
            <input
              type="number"
              id={`rate-consumption-${rate.label}`}
              className="rate-row__rate-consumption"
              value={rate.consumption}
              disabled
              title="Pegged to the total consumption of the commodity rates in this option"
              aria-label="Rate consumption (pegged to total commodity consumption)"
            />
          ) : (
            <input
              type="number"
              id={`rate-consumption-${rate.label}`}
              className={`rate-row__rate-consumption ${hasError ? "is-invalid" : ""}`}
              placeholder="e.g. 12000"
              value={consumptionInput}
              onChange={(e) => {
                handleConsumptionChange(e.target.value);
              }}
              aria-label="Rate consumption"
            />
          )}
        </div>
      )}

      {getUnitByLabel(rate.unit)?.hasPeriodSelector && (
        <div className="rate-row__field-group">
          <label className="rate-row__label" htmlFor={`rate-period-${rate.label}`}>
            Period
          </label>
          <select
            id={`rate-period-${rate.label}`}
            className="rate-row__kva-period-select"
            value={rate.kvaPeriod}
            onChange={(e) => {
              handleKvaPeriodChange(e.target.value as KvaPeriod);
            }}
            aria-label="Capacity period"
          >
            {KVA_PERIOD_OPTIONS.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
      )}

      <div className="rate-row__field-group">
        <label className="rate-row__label" htmlFor={`rate-unit-${rate.label}`}>
          Unit
        </label>
        <select
          id={`rate-unit-${rate.label}`}
          className="rate-row__unit-select"
          value={rate.unit}
          onChange={(e) => {
            handleUnitChange(e.target.value);
          }}
          aria-label="Rate unit"
        >
          {AVAILABLE_UNITS.map((u) => (
            <option key={u.id} value={u.label}>
              {u.label}
            </option>
          ))}
        </select>
      </div>

      <div className="rate-row__field-group">
        <label className="rate-row__label" htmlFor={`rate-cost-${rate.label}`}>
          Est. Annual Cost
        </label>
        <input
          type="text"
          id={`rate-cost-${rate.label}`}
          className="rate-row__estimated-cost"
          value={`£${rate.estimatedAnnualCost.toFixed(2)}`}
          disabled
          aria-label="Estimated annual cost"
        />
      </div>

      <div className="rate-row__field-group rate-row__hidden-toggle">
        <label className="rate-row__label" htmlFor={`rate-hidden-${rate.label}`}>
          Hidden
        </label>
        <div className="rate-row__hidden-toggle-control">
          <input
            id={`rate-hidden-${rate.label}`}
            type="checkbox"
            checked={rate.hidden}
            onChange={(e) => {
              handleHiddenChange(e.target.checked);
            }}
          />
        </div>
      </div>

      <button
        type="button"
        className="rate-row__remove-button"
        onClick={onRemove}
        aria-label="Remove this rate"
      >
        ×
      </button>

      {errorMessage && <div className="invalid-feedback d-block">{errorMessage}</div>}
    </div>
  );
};

export default RateRow;
